/**
 * TouchCampaignGraph
 *
 * Editable, pannable node canvas for a touch campaign's steps. Replaces the
 * vertical `TemplateBlocksSection` list for touch templates: each touch is a
 * card on an infinite canvas (`TouchGraphCanvas`) you can pan / zoom, and
 * selecting a card opens its editor in an off-canvas panel that always renders
 * at 100% scale (so TipTap rich-text editing isn't fighting the zoom
 * transform).
 *
 * Reuses the exact create / update / delete wiring and pure step-plan helpers
 * that `TemplateBlocksSection` uses, so both views stay behaviourally identical
 * — only the layout differs.
 */
import type { ReactNode } from 'react';
import { documentThumbnailUrl, updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RouterInputs } from '@poseidon/api/types';
import type { CadenceStepTypePure } from '@poseidon/components/CGStep';
import type { StepType } from '@poseidon/components/CGStep/CTSContentEditingPure';
import type CadenceStepOption from '@poseidon/types/pg-gen/cadenceStepOption';
import CTSContentEditingPure from '@poseidon/components/CGStep/CTSContentEditingPure';
import { cn } from '@poseidon/components/cn';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import { Button } from '@poseidon/components/Input2';
import Popup from '@poseidon/components/Popup/Popup';
import { Alerts, General } from '@poseidon/components/StrokeIcons';
import { TemplateStepPickerPure } from '@poseidon/components/TemplateBlockCard';
import { ContactAtVariables, UserAtVariables } from '@poseidon/types/types/atVariables.type';

import type { TouchFlowContact } from './ContactFacePile';
import type { InlineEditor } from './TouchGraphCanvas';
import type { StatusByTemplateStepId } from './touchGraphLayout';
import type { TemplateBlocksSectionProps } from '~/components/TemplateBlocksSection/TemplateBlocksSection';
import {
  useCadenceTemplateCreateStepMutation,
  useCadenceTemplateDeleteStepMutation,
  useCadenceUpdateTemplateStepMutation,
} from '@/hooks/trpc/useCadenceTemplateMutation';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import {
  CONTENT_H as FLOW_CONTENT_H,
  CONTENT_W as FLOW_CONTENT_W,
} from '~/components/CandidateFlowGraph/candidateFlow.graph';
import {
  findPreviousEmailSubject,
  findPreviousEmailSubjectForNewStep,
} from '~/components/CTGStep/previousEmailSubject';
import {
  buildAddParamsFromEditor,
  buildFastCreateParams,
  collectStepsToDeleteForBlock,
  computeExistingStepTypes,
  planStepCreate,
} from '~/components/TemplateBlocksSection/templateBlocksLogic';
import { TouchGraphCanvas } from './TouchGraphCanvas';
import {
  ARM_COLUMN_X0,
  buildTouchGraphModel,
  FORK_ARM_OPTIONS,
  TOUCH_NODE_W,
  TOUCH_WAIT_GAP,
} from './touchGraphLayout';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
/** Width of the inline editor card on the canvas (content space). */
const EDITOR_W = 400;

type AddStepParams = RouterInputs['cadence']['createStep']['data'];
type UpdateTemplateStepParams = RouterInputs['cadenceTemplate']['updateStep']['data'];

type Document = NonNullable<TemplateBlocksSectionProps['documents']>[number];

const userVarsSet = new Set<string>(UserAtVariables);
const contactVarsSet = new Set<string>(ContactAtVariables);

const convertAtVariables = (vars: Record<string, string>) =>
  Object.keys(vars).map((id) => {
    let group: 'user' | 'contact' | undefined;
    if (userVarsSet.has(id) || id === 'Today') group = 'user';
    else if (contactVarsSet.has(id)) group = 'contact';
    return { id, label: vars[id] ?? id, group };
  });

const convertDocuments = (docs: Document[] | undefined) =>
  docs
    ? docs
        .filter((d) => !('isPrivate' in d))
        .map((d) => ({ id: `${d.id}`, label: d.title ?? 'Unnamed', imageUrl: documentThumbnailUrl(d) }))
    : [];

/**
 * Editor-panel state. `null` = nothing open (canvas full-width). `edit` targets
 * an existing step; `create` opens the picker-less editor for a brand new touch
 * anchored either after the last touch or before an existing one.
 */
type EditorState =
  | { mode: 'edit'; stepId: string }
  // Create a step. `delay`/`delayAlignWeek` are the wait for the new block (0 for
  // a same-day add). `anchorBlockId` set = adding into an existing touch (same day).
  // `option: 'two'` routes the step onto a connect touch's not-accepted (No) branch.
  // `option: 'three'` forks a new PARALLEL arm off a delay touch (a new day on its
  // own option, running alongside the main spine).
  // `anchorX`/`anchorY` position the editor at a free spot (e.g. the No stub) with
  // no node to hide.
  | {
      mode: 'create';
      afterStepId?: string;
      beforeStepId?: string;
      delay: number;
      delayAlignWeek: boolean;
      anchorBlockId?: string;
      option?: CadenceStepOption;
      anchorX?: number;
      anchorY?: number;
      /** Set when appending to a fork arm — the arm-tail add-node to hide (its editor replaces it). */
      armTailBlockId?: string;
    };

export function TouchCampaignGraph({
  userTeam,
  cadenceTemplate,
  atVariables,
  documents,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  renderSiftConditionBuilder,
  embedded = false,
  hideCandidates = false,
  disableWheelPan = false,
  showViewToggle = false,
  defaultMinimal = false,
  minimal: controlledMinimal,
  contactStepStatuses,
  onMarkStepFinished,
}: TemplateBlocksSectionProps & {
  embedded?: boolean;
  /** Suppress the campaign-wide "Candidates" header (e.g. on a single contact's page). */
  hideCandidates?: boolean;
  /** Let the mouse wheel scroll the host page instead of panning (drag-only). */
  disableWheelPan?: boolean;
  /** Show the Minimal / Expanded view-mode toggle on the canvas. */
  showViewToggle?: boolean;
  /** Start in Minimal (compact) mode. Only meaningful with `showViewToggle`. */
  defaultMinimal?: boolean;
  /**
   * CONTROLLED minimal state — when set, the parent owns Minimal ↔ Expanded (e.g. the
   * campaign switch, whose one toggle drives every embedded destination together) and
   * the on-canvas toggle is bypassed. Omit to use the internal `defaultMinimal` state.
   */
  minimal?: boolean;
  /**
   * Per-contact step status keyed by template step id — overlays done / action-needed
   * / errored / skipped on the (otherwise template-shaped) graph. Contact page only;
   * omit on the template page so the graph renders with no status.
   */
  contactStepStatuses?: StatusByTemplateStepId | null;
  /** Complete an action-required step for this contact (its own cadence-step id). */
  onMarkStepFinished?: (cadenceStepId: string) => void;
}) {
  const createMutation = useCadenceTemplateCreateStepMutation();
  const updateMutation = useCadenceUpdateTemplateStepMutation();
  const deleteMutation = useCadenceTemplateDeleteStepMutation();

  const graph = cadenceTemplate.stepsGraph;

  // The candidate → enrollment pipeline that feeds this campaign. Shown as a
  // collapsible "Candidates" header above the first touch for teams on the
  // candidate-staging flow. Never shown when embedded (e.g. a campaign-switch
  // destination — the switch itself is the enrollment mechanism).
  const candidatesEnabled = !embedded && !hideCandidates && userTeam.campaignPrefilterEnabled === true;
  const [candidatesExpanded, setCandidatesExpanded] = useState(false);
  // Minimal (compact icon nodes) vs Expanded (full cards). Node heights depend on
  // it, so the model is rebuilt when it flips. Both modes render the same branching
  // graph — only node density changes. A `minimal` prop makes it controlled (parent
  // owns the toggle); otherwise it's internal state seeded by `defaultMinimal`.
  const [uncontrolledMinimal, setMinimal] = useState(defaultMinimal);
  const minimal = controlledMinimal ?? uncontrolledMinimal;

  // "Show contacts in this flow" overlay — face-pile every actively-enrolled
  // contact next to the touch they're currently at. Template page only: on a
  // single contact's page (contactStepStatuses != null) their own status is
  // already overlaid, and the embedded switch canvas has no enrollment of its own.
  const canShowContacts = !embedded && contactStepStatuses == null;
  const [showContacts, setShowContacts] = useState(false);
  const contactsInFlowQuery = api.cadence.contactsInFlow.useQuery(
    { cadenceTemplateId: cadenceTemplate.id },
    { enabled: canShowContacts && showContacts },
  );
  const candidateCountsQuery = api.cadenceTemplate.candidateCounts.useQuery(
    { cadenceTemplateId: cadenceTemplate.id },
    { enabled: candidatesEnabled },
  );
  const candidateCounts = candidateCountsQuery.data?.counts;
  const candidatesTotal = Object.values(candidateCounts ?? {}).reduce((a, b) => a + (b ?? 0), 0);
  const candidatesError =
    (candidateCounts?.research_failed ?? 0) +
    (candidateCounts?.synthesize_failed ?? 0) +
    (candidateCounts?.personalize_failed ?? 0);
  const hasPersonalizedSteps = graph != null && Object.values(graph.nodes).some((n) => n.isPersonalized);

  // Destination options for the switch_campaign step's target picker (the team's other
  // templates). The graph only renders for touch campaigns, so this is inherently gated.
  const campaignTemplatesQuery = api.cadenceTemplate.namesWithOwner.useQuery({
    teamId: cadenceTemplate.teamId,
    pagination: { limit: 5000, offset: 0 },
  });
  // Template id → name, used to resolve a switch_campaign step's destination for display.
  const campaignNameById = useMemo(
    () => new Map((campaignTemplatesQuery.data?.data ?? []).map((t) => [t.id, t.name])),
    [campaignTemplatesQuery.data],
  );

  const candW = candidatesEnabled ? (candidatesExpanded ? FLOW_CONTENT_W : TOUCH_NODE_W) : null;
  const candH = candidatesEnabled ? (candidatesExpanded ? FLOW_CONTENT_H : 84) : null;
  const model = useMemo(
    () =>
      buildTouchGraphModel(
        graph,
        candW != null && candH != null ? { w: candW, h: candH } : null,
        minimal,
        contactStepStatuses ?? null,
        campaignNameById,
      ),
    [graph, candW, candH, minimal, contactStepStatuses, campaignNameById],
  );

  // Map every enrolled contact onto the graph node it currently sits at. A node owns
  // its delay-block id, its folded wait_for_connect id, and each of its action-step
  // ids — a contact's `templateStepId` matches exactly one of those. Keyed by blockId
  // so the canvas can pile faces beside each node.
  const contactsByBlockId = useMemo(() => {
    if (!showContacts) return null;
    const blockIdByTemplateStepId = new Map<string, string>();
    for (const node of model.nodes) {
      blockIdByTemplateStepId.set(node.blockId, node.blockId);
      if (node.wfcId != null) blockIdByTemplateStepId.set(node.wfcId, node.blockId);
      for (const step of node.steps) blockIdByTemplateStepId.set(step.id, node.blockId);
    }
    const byBlock = new Map<string, TouchFlowContact[]>();
    for (const c of contactsInFlowQuery.data?.contacts ?? []) {
      if (c.templateStepId == null) continue;
      const blockId = blockIdByTemplateStepId.get(c.templateStepId);
      if (blockId == null) continue;
      const list = byBlock.get(blockId) ?? [];
      list.push({
        cadenceId: c.cadenceId,
        contactId: c.contactId,
        firstName: c.firstName,
        lastName: c.lastName,
        imageSrc: c.imageSrc,
        orgImageSrc: c.orgImageSrc,
      });
      byBlock.set(blockId, list);
    }
    return byBlock;
  }, [showContacts, model.nodes, contactsInFlowQuery.data]);

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [pendingDeleteBlockId, setPendingDeleteBlockId] = useState<string | null>(null);

  const variables = useMemo(() => convertAtVariables(atVariables), [atVariables]);
  const tipTapDocuments = useMemo(() => convertDocuments(documents), [documents]);

  // Admin-only resolved-model banner — same gate as TemplateBlocksSection.
  const [auth] = useAuthContext();
  const viewerIsAdminLike = ('userTeam' in auth && auth.userTeam?.isSuperAdmin === true) || auth.impersonation != null;
  const aiModelsQuery = api.superAdmin.getAiModelConfigs.useQuery(undefined, {
    enabled: viewerIsAdminLike,
    staleTime: 5 * 60 * 1000,
  });
  const personalizeModelInfo = useMemo<{ modelId: string } | null>(() => {
    if (!viewerIsAdminLike) return null;
    const cfg = aiModelsQuery.data?.configs.find((c) => c.key === 'personalize_template_step');
    return cfg ? { modelId: cfg.model_id } : null;
  }, [viewerIsAdminLike, aiModelsQuery.data]);

  // Step types already in the template — used to disable once-only types
  // (connect / follow / like / sift) in the new-touch picker.
  const existingTypes = useMemo<readonly CadenceStepTypePure[]>(
    () => (graph == null ? [] : computeExistingStepTypes(graph)),
    [graph],
  );

  // Resolve the currently-edited step + the node (block) it belongs to.
  const selectedStepId = editor?.mode === 'edit' ? editor.stepId : null;
  const selected = useMemo(() => {
    if (editor?.mode !== 'edit') return null;
    // Not-accepted touches are their own nodes, so a single `steps` scan covers
    // both the accepted spine and the No branch.
    for (const node of model.nodes) {
      const step = node.steps.find((s) => s.id === editor.stepId);
      if (step != null) return { node, step };
    }
    return null;
  }, [editor, model.nodes]);

  // If the edited step vanishes (deleted, or removed by a refetch), close the inline
  // editor rather than leaving it anchored to nothing.
  useEffect(() => {
    if (editor?.mode === 'edit' && selected == null) setEditor(null);
  }, [editor, selected]);

  const closeEditor = useCallback(() => setEditor(null), []);

  // Escape cancels the inline editor (the "New touch" / step popup). Register on the
  // shared esc stack at INTERSTITIAL (same priority as <Popup>) so it's the topmost
  // handler — Escape closes the editor instead of falling through to ContactDetailPage
  // and closing the whole detail panel. The id is per-template so two graphs mounted at
  // once (e.g. a switch-campaign canvas) don't deregister each other on close.
  useEscStack(
    `TOUCH_GRAPH_EDITOR_${cadenceTemplate.id}`,
    ESC_PRIORITY.INTERSTITIAL,
    editor != null ? closeEditor : undefined,
  );

  const onCreateStep = async (data: AddStepParams) =>
    updateWithToast({
      loading: 'Creating touch',
      success: 'Touch added',
      mutationFnAsync: () => createMutation.mutateAsync({ cadenceTemplateId: cadenceTemplate.id, data }),
    });

  const onUpdateStep = async (stepId: string, data: UpdateTemplateStepParams) =>
    updateWithToast({
      loading: 'Updating touch',
      success: 'Touch updated',
      mutationFnAsync: () =>
        updateMutation.mutateAsync({
          teamId: cadenceTemplate.teamId,
          cadenceTemplateId: cadenceTemplate.id,
          stepId,
          data,
        }),
    });

  const onDeleteStep = async (stepId: string) =>
    updateWithToast({
      loading: 'Deleting',
      success: 'Deleted',
      mutationFnAsync: () =>
        deleteMutation.mutateAsync({ teamId: cadenceTemplate.teamId, cadenceTemplateId: cadenceTemplate.id, stepId }),
    });

  // Edit the wait leading into a touch (the "wait N weeks" chip on the edge).
  const onChangeWait = async (blockId: string, days: number, alignWeek: boolean) =>
    onUpdateStep(blockId, { delay: days * DAY_MS, delayAlignWeek: alignWeek });

  const handleAddTouch = () => {
    // Anchor on the last MAIN-spine touch. The trailing "Add a touch" button sits under
    // the spine, but fork-arm / No-branch nodes are pushed AFTER the spine into
    // model.nodes — so model.nodes.at(-1) could be an arm tail, growing the wrong chain.
    const mainNodes = model.nodes.filter((n) => n.branch === 'main');
    const lastNode = mainNodes[mainNodes.length - 1];
    // After a connect touch (whose next.one is its wait_for_connect), the new
    // touch is the ACCEPTED (Yes) continuation — anchor on the wfc with
    // option='one' so it doesn't collide with the wfc. Otherwise anchor on the
    // last block's delay id (the normal fresh-day path). Empty campaign → first touch.
    const yesAnchor = lastNode?.waitsForConnect ? lastNode.wfcId : null;
    setEditor({
      mode: 'create',
      afterStepId: yesAnchor ?? lastNode?.blockId,
      delay: lastNode == null ? 0 : WEEK_MS,
      delayAlignWeek: lastNode != null,
      option: yesAnchor != null ? 'one' : undefined,
    });
  };

  const handleInsertBefore = (blockId: string) => {
    // The inserted touch takes the target's slot. If the target currently runs right
    // away — the first main touch, or a parallel fork arm's head — the new touch runs
    // right away too; otherwise inserting would push a week's wait ABOVE it and the
    // first/arm would no longer fire immediately. The target then follows the new touch
    // (server re-parents it under the new delay), and its now-editable "runs right away"
    // edge lets a wait be added, so A → (runs right away) → C → wait → B is reachable.
    const target = model.nodes.find((n) => n.blockId === blockId);
    const runsRightAway = target == null || target.delayMs <= 0;
    setEditor({
      mode: 'create',
      beforeStepId: blockId,
      delay: runsRightAway ? 0 : WEEK_MS,
      delayAlignWeek: !runsRightAway,
    });
  };

  // Add another step INTO an existing touch (same day, no new wait): anchor on the
  // touch's last runnable step so the server appends a sibling in the same block.
  const handleAddStep = (blockId: string, lastStepId: string) => {
    setEditor({ mode: 'create', afterStepId: lastStepId, delay: 0, delayAlignWeek: false, anchorBlockId: blockId });
  };

  // Start a connect touch's not-accepted (No) branch from its stub: anchor the
  // editor at the stub (in the No column) — not the connect touch — and route the
  // step onto the No branch (option='two' on the wfc).
  const handleAddNoBranchStep = (blockId: string, wfcId: string) => {
    const stub = model.noStubs.find((s) => s.connectBlockId === blockId);
    setEditor({
      mode: 'create',
      afterStepId: wfcId,
      delay: 0,
      delayAlignWeek: false,
      option: 'two',
      anchorX: stub != null ? stub.x + stub.w / 2 : undefined,
      anchorY: stub?.y,
    });
  };

  // Start a BRANCH off a touch: when its step group finishes, run a new arm in parallel.
  // Each arm is a new "day" under the touch's delay node on its own option — 'three'..
  // 'six' (option 'one' is the main spine, 'two' the wfc No-branch). A touch can fork up
  // to FORK_ARM_OPTIONS.length arms; assign the first free option and bail once they're
  // all used. Branch-start delay is 0 so the arm auto-runs the moment the group finishes
  // (that's the point of a parallel arm); waits are added per-step afterward.
  const handleAddForkArm = (blockId: string) => {
    const delayNode = graph?.nodes[blockId] ?? null;
    // The first arm option whose child slot is still empty on this touch's delay node.
    const nextOption = FORK_ARM_OPTIONS.find((opt) => delayNode == null || delayNode.next[opt] == null);
    if (nextOption == null) return; // all arm slots taken
    // Float the editor right beside the touch it forks from — the first arm column,
    // just past the spine — aligned with the touch's top. It's a transient popup, so
    // keep it next to where the branch was triggered rather than chasing the arm's
    // eventual (possibly further-right) column, which reads as "miles away" in the
    // narrow embedded switch view.
    const forkNode = model.nodes.find((n) => n.blockId === blockId);
    setEditor({
      mode: 'create',
      afterStepId: blockId,
      delay: 0,
      delayAlignWeek: false,
      option: nextOption,
      anchorX: forkNode != null ? forkNode.x + ARM_COLUMN_X0 + TOUCH_NODE_W / 2 : undefined,
      anchorY: forkNode != null ? forkNode.y : undefined,
    });
  };

  // Extend a branch: append another step to the arm's TAIL block. Anchors on the
  // tail delay node with option 'one' so the server appends a new day onto the arm
  // chain. The editor floats in the arm's column (below the tail). Week-paced to
  // match the touch campaign's weekly cadence — the same default every other
  // new-day add uses (handleAddTouch / handleInsertBefore), so arms don't silently
  // fall back to a 1-day wait while the main spine waits a week.
  const handleAddArmStep = (blockId: string) => {
    const tail = model.nodes.find((n) => n.blockId === blockId);
    setEditor({
      mode: 'create',
      afterStepId: blockId,
      delay: WEEK_MS,
      delayAlignWeek: true,
      option: 'one',
      anchorX: tail != null ? tail.x + tail.w / 2 : undefined,
      anchorY: tail != null ? tail.y + tail.h + TOUCH_WAIT_GAP : undefined,
      armTailBlockId: blockId,
    });
  };

  // Parallel fork arms running off a touch. Deleting a touch does NOT sweep up its
  // arm subtrees (collectStepsToDeleteForBlock only handles the touch's own group +
  // the connect No-branch), so a touch that still has arms is blocked from deletion
  // until the arms are removed — otherwise they'd be orphaned. Arm heads carry
  // edgeKind 'fork'; the connect No-branch ('notAccepted') is intentionally
  // cascade-deleted, so it is NOT counted here.
  const forkArmCountForBlock = (blockId: string) =>
    model.nodes.filter((n) => n.edgeKind === 'fork' && n.parentBlockId === blockId).length;

  const handleDeleteBlock = async () => {
    const blockId = pendingDeleteBlockId;
    if (blockId == null) return;
    // Safety net — the confirm dialog already blocks this, but never orphan arms.
    if (forkArmCountForBlock(blockId) > 0) return;
    setPendingDeleteBlockId(null);
    if (selected?.node.blockId === blockId) closeEditor();
    const node = model.nodes.find((n) => n.blockId === blockId);
    // The No-branch runnables come first, then the connect — so when the server
    // deletes the connect step it also clears its wait_for_connect (which it does
    // for connect deletes), with no leftover not-accepted steps to block it.
    const ids = collectStepsToDeleteForBlock(
      { id: blockId, delay: 0, group: node?.steps.map((s) => s.id) ?? [], nextOne: null },
      graph,
    );
    for (const id of ids) await onDeleteStep(id);
  };

  // Destination options for the switch_campaign step's target picker — the team's other
  // templates (its own id filtered out; a step can't switch into its own campaign).
  const campaignTemplateOptions = useMemo(
    () =>
      (campaignTemplatesQuery.data?.data ?? [])
        .filter((t) => t.id !== cadenceTemplate.id)
        .map((t) => ({ value: t.id, label: t.name })),
    [campaignTemplatesQuery.data, cadenceTemplate.id],
  );

  const editorExtras = {
    variables,
    documents: tipTapDocuments,
    reminderActions,
    reminderActionsLoading,
    onCreateReminder,
    reminderCreating,
    onReminderError,
    renderSiftConditionBuilder: renderSiftConditionBuilder ?? (() => null),
    canPersonalize: userTeam.campaignPrefilterEnabled === true,
    personalizeModelInfo,
    campaignTemplateOptions,
  };

  const pendingDeleteNode = model.nodes.find((n) => n.blockId === pendingDeleteBlockId);
  const pendingDeleteArmCount = pendingDeleteBlockId == null ? 0 : forkArmCountForBlock(pendingDeleteBlockId);

  // Build the inline editor anchored to the touch it edits (or the add / insert
  // slot for a new one). Rendered on the canvas by TouchGraphCanvas.
  let inlineEditor: InlineEditor | null = null;
  if (editor?.mode === 'edit' && selected != null) {
    inlineEditor = {
      key: `edit:${selected.step.id}`,
      cx: selected.node.x + selected.node.w / 2,
      y: selected.node.y,
      w: EDITOR_W,
      hideBlockId: selected.node.blockId,
      children: (
        <EditorCard title="Edit touch" onClose={closeEditor}>
          <CTSContentEditingPure
            key={selected.step.id}
            stepType={selected.step.type as StepType}
            stepData={{
              subject: selected.step.subject,
              body: selected.step.body2,
              postsToLike: selected.step.postsToLike,
              cc: selected.step.cc,
              bcc: selected.step.bcc,
              siftConditions: selected.step.siftConditions,
              onlyOpenLink: selected.step.onlyOpenLink,
              isPersonalized: selected.step.isPersonalized,
              personalizationPrompt: selected.step.personalizationPrompt,
              replyToPrevious: selected.step.replyToPrevious,
              autoWithdraw: selected.step.autoWithdraw,
              // switch_campaign only: preload the current destination so editing doesn't blank it out.
              triggerCadenceTemplateIds: selected.step.triggerCadenceTemplateIds,
            }}
            // Email steps offer a "continue previous thread" toggle when an earlier email
            // exists; the subject of that email is what a threading step inherits.
            previousEmailSubject={
              selected.step.type === 'email' && graph != null
                ? findPreviousEmailSubject(graph.nodes, selected.step.id)
                : null
            }
            variables={editorExtras.variables}
            documents={editorExtras.documents}
            reminderActions={editorExtras.reminderActions}
            reminderActionsLoading={editorExtras.reminderActionsLoading}
            onCreateReminder={editorExtras.onCreateReminder}
            reminderCreating={editorExtras.reminderCreating}
            onReminderError={editorExtras.onReminderError}
            renderSiftConditionBuilder={editorExtras.renderSiftConditionBuilder}
            canPersonalize={editorExtras.canPersonalize}
            personalizeModelInfo={editorExtras.personalizeModelInfo}
            campaignTemplateOptions={editorExtras.campaignTemplateOptions}
            submitLabel="Save"
            onSubmit={async (data) => {
              const stepId = selected.step.id;
              await onUpdateStep(stepId, {
                body2: data.body ?? undefined,
                subject: data.subject ?? undefined,
                postsToLike: data.postsToLike ?? undefined,
                cc: data.cc ?? undefined,
                bcc: data.bcc ?? undefined,
                onlyOpenLink: data.onlyOpenLink ?? undefined,
                siftConditions: (data.siftConditions ?? undefined) as UpdateTemplateStepParams['siftConditions'],
                isPersonalized: data.isPersonalized ?? undefined,
                personalizationPrompt: data.personalizationPrompt ?? undefined,
                replyToPrevious: data.replyToPrevious ?? undefined,
                autoWithdraw: data.autoWithdraw ?? undefined,
                // switch_campaign only: persist the (edited) destination campaign.
                triggerCadenceTemplateIds: data.triggerCadenceTemplateIds ?? undefined,
              });
              closeEditor();
            }}
            onDelete={() => {
              const step = selected.step;
              const blockId = selected.node.blockId;
              closeEditor();
              // A connect step owns its wait_for_connect (and any not-accepted steps),
              // so deleting it means deleting the whole touch — route through the
              // block-delete confirm which cleans up the No branch first.
              if (step.type === 'connect') {
                setPendingDeleteBlockId(blockId);
                return;
              }
              // Otherwise delete just this step; if it's the touch's only step the
              // server auto-removes the now-empty day.
              void onDeleteStep(step.id);
            }}
            onCancel={closeEditor}
          />
        </EditorCard>
      ),
    };
  } else if (editor?.mode === 'create') {
    // Free-position anchor (No-branch stub) wins; otherwise anchor on the target
    // block for insert-before / same-day add, else the trailing add-node.
    const anchorBlockId = editor.beforeStepId ?? editor.anchorBlockId;
    const anchorNode = anchorBlockId != null ? model.nodes.find((n) => n.blockId === anchorBlockId) : null;
    const fallback = anchorNode ?? { x: model.addNode.x, y: model.addNode.y, w: model.addNode.w };
    const isSameDay = editor.anchorBlockId != null;
    const isArmStep = editor.armTailBlockId != null;
    // A parallel-branch start floats via anchorX in the arm column. Its option is the
    // first free fork-arm slot (three..six) — NOT always 'three' once a touch has 2+ arms.
    const isForkStart = editor.anchorX != null && !isArmStep && FORK_ARM_OPTIONS.some((o) => o === editor.option);
    // A No-branch start also floats via anchorX; distinguish it from a fork / arm-append.
    const isNoStart = editor.anchorX != null && !isArmStep && !isForkStart;
    inlineEditor = {
      key: isArmStep
        ? `create:arm:${editor.armTailBlockId}`
        : isForkStart
          ? `create:fork:${editor.afterStepId}`
          : isNoStart
            ? `create:no:${editor.afterStepId}`
            : isSameDay
              ? `create:instep:${editor.anchorBlockId}`
              : editor.beforeStepId != null
                ? `create:before:${editor.beforeStepId}`
                : 'create:add',
      cx: editor.anchorX ?? fallback.x + fallback.w / 2,
      y: editor.anchorY ?? fallback.y,
      w: EDITOR_W,
      // Trailing create swaps in for the add-node; a same-day add replaces the touch;
      // a No-branch / fork start / arm-append float (hide nothing on the main spine).
      hideAdd: editor.beforeStepId == null && editor.anchorBlockId == null && !isNoStart && !isForkStart && !isArmStep,
      hideBlockId: editor.anchorBlockId,
      // Appending to an arm replaces THAT arm's add-node (others stay).
      hideArmTailBlockId: editor.armTailBlockId,
      children: (
        <EditorCard
          title={isNoStart ? 'If not accepted' : isForkStart ? 'New branch' : isSameDay ? 'Add a step' : 'New touch'}
          onClose={closeEditor}>
          <CreateTouchEditor
            delay={editor.delay}
            delayAlignWeek={editor.delayAlignWeek}
            afterStepId={editor.afterStepId}
            beforeStepId={editor.beforeStepId}
            option={editor.option}
            existingTypes={existingTypes}
            voiceEnabled={userTeam.voiceEnabled}
            voiceCallingEnabled={userTeam.voiceCallingEnabled}
            // Touch Campaign Graph is the only surface that offers "Switch to another campaign"
            // (place it on a connect step's Yes / No branch). It's a terminal action, so it must
            // be the ONLY step in its touch — offer it only when starting a NEW (empty) touch,
            // never as a same-day add into a touch that already has steps.
            allowSwitchCampaign={!isSameDay}
            editorExtras={editorExtras}
            // Email touches offer a "continue previous thread" toggle when an earlier email exists;
            // resolve the subject it would inherit from where this new step is being inserted.
            previousEmailSubject={
              graph != null
                ? findPreviousEmailSubjectForNewStep(graph.nodes, {
                    afterStepId: editor.afterStepId,
                    beforeStepId: editor.beforeStepId,
                  })
                : null
            }
            onCreateStep={onCreateStep}
            onDone={closeEditor}
          />
        </EditorCard>
      ),
    };
  }

  return (
    <div className={cn('relative', !embedded && 'flex size-full min-h-0 flex-1 overflow-hidden')}>
      <TouchGraphCanvas
        embedded={embedded}
        minimal={minimal}
        // When minimal is controlled by the parent, don't wire the on-canvas toggle —
        // it would call setMinimal on state that no longer drives `minimal`, i.e. a
        // dead toggle. Omitting the handler hides the toggle instead (TouchGraphCanvas
        // gates it on onSetMinimal != null).
        onSetMinimal={controlledMinimal == null ? setMinimal : undefined}
        showViewToggle={showViewToggle}
        disableWheelPan={disableWheelPan}
        model={model}
        selectedStepId={selectedStepId}
        onMarkStepFinished={onMarkStepFinished}
        onSelectStep={(stepId) => setEditor({ mode: 'edit', stepId })}
        onDeleteBlock={(blockId) => setPendingDeleteBlockId(blockId)}
        onAddStep={handleAddStep}
        onAddNoBranchStep={handleAddNoBranchStep}
        onAddForkArm={handleAddForkArm}
        onAddArmStep={handleAddArmStep}
        onAddTouch={handleAddTouch}
        onInsertBefore={handleInsertBefore}
        onChangeWait={onChangeWait}
        onChangeAutoWithdraw={(connectStepId, value) => onUpdateStep(connectStepId, { autoWithdraw: value })}
        addLabel={model.nodes.length === 0 ? 'Add the first step' : 'Add a step'}
        inlineEditor={inlineEditor}
        contactsByBlockId={contactsByBlockId}
        showContacts={canShowContacts ? showContacts : undefined}
        onToggleContacts={canShowContacts ? () => setShowContacts((v) => !v) : undefined}
        contactsCount={contactsInFlowQuery.data?.contacts.length ?? null}
        contactsLoading={showContacts && contactsInFlowQuery.isPending}
        candidates={
          candidatesEnabled
            ? {
                expanded: candidatesExpanded,
                onToggle: () => setCandidatesExpanded((v) => !v),
                total: candidatesTotal,
                errorCount: candidatesError,
                counts: candidateCounts,
                hasFilterRules: candidateCountsQuery.data?.hasFilterRules ?? false,
                hasPersonalizedSteps,
              }
            : null
        }
      />

      {pendingDeleteBlockId != null && (
        <Popup onClose={() => setPendingDeleteBlockId(null)} className="max-w-sm p-0">
          {/* Blocked: the touch still has parallel branches. Deleting it would orphan
              their arm subtrees, so refuse until the branches are removed. */}
          {pendingDeleteArmCount > 0 && (
            <>
              <div className="flex flex-col items-center gap-2 px-5 pt-5">
                <div className="flex size-9 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
                  <Alerts.AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete its branches first</h3>
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  {pendingDeleteArmCount === 1
                    ? 'This touch has a parallel branch running off it. Delete that branch before deleting this touch.'
                    : `This touch has ${pendingDeleteArmCount} parallel branches running off it. Delete them before deleting this touch.`}
                </p>
              </div>
              <div className="flex justify-end px-5 py-4">
                <Button size="sm" onClick={() => setPendingDeleteBlockId(null)}>
                  Got it
                </Button>
              </div>
            </>
          )}

          {pendingDeleteArmCount === 0 && (
            <>
              <div className="flex flex-col items-center gap-2 px-5 pt-5">
                <div className="flex size-9 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                  <Alerts.AlertTriangle className="size-4 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete this touch?</h3>
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  {(pendingDeleteNode?.steps.length ?? 0) <= 1
                    ? 'This removes the touch from the campaign. This cannot be undone.'
                    : `This removes all ${pendingDeleteNode?.steps.length} steps in this touch. This cannot be undone.`}
                </p>
              </div>
              <div className="flex justify-end gap-2 px-5 py-4">
                <Button size="sm" onClick={() => setPendingDeleteBlockId(null)}>
                  Cancel
                </Button>
                <Button size="sm" color="red" icon={<General.Trash01 />} onClick={() => void handleDeleteBlock()}>
                  Delete
                </Button>
              </div>
            </>
          )}
        </Popup>
      )}
    </div>
  );
}

type EditorExtras = {
  variables: ReturnType<typeof convertAtVariables>;
  documents: ReturnType<typeof convertDocuments>;
  reminderActions: TemplateBlocksSectionProps['reminderActions'];
  reminderActionsLoading: TemplateBlocksSectionProps['reminderActionsLoading'];
  onCreateReminder: TemplateBlocksSectionProps['onCreateReminder'];
  reminderCreating: TemplateBlocksSectionProps['reminderCreating'];
  onReminderError: TemplateBlocksSectionProps['onReminderError'];
  renderSiftConditionBuilder: NonNullable<TemplateBlocksSectionProps['renderSiftConditionBuilder']>;
  canPersonalize: boolean;
  personalizeModelInfo: { modelId: string } | null;
  campaignTemplateOptions: { value: string; label: string }[];
};

/** Floating card chrome for the inline editor — a titled header with a close X. */
function EditorCard({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-lynch-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-lynch-200 px-3 py-2 dark:border-slate-700">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</span>
        <Button
          icon={<General.XMark />}
          text
          size="xs"
          tooltip="Close"
          onClick={onClose}
          data-testid="touch-editor-close"
        />
      </div>
      <div className="flex max-h-[76vh] flex-col gap-3 overflow-y-auto p-3">{children}</div>
    </div>
  );
}

/**
 * New-touch editor. Touch campaigns are no longer locked to one step type, so a
 * touch starts with the full step-type picker (once-only types already in the
 * template are disabled). Fast-create types (follow / like / visit) create on
 * pick; content types drop into the inline editor.
 */
function CreateTouchEditor({
  delay,
  delayAlignWeek,
  afterStepId,
  beforeStepId,
  option,
  existingTypes,
  voiceEnabled,
  voiceCallingEnabled,
  allowSwitchCampaign,
  editorExtras,
  previousEmailSubject,
  onCreateStep,
  onDone,
}: {
  /** Wait for the new step's block (0 = same day as its anchor). */
  delay: number;
  delayAlignWeek: boolean;
  afterStepId?: string;
  beforeStepId?: string;
  /**
   * Branch routing — 'two' puts the step on a connect touch's not-accepted branch;
   * 'three' forks a parallel arm off a delay touch.
   */
  option?: CadenceStepOption;
  existingTypes: readonly CadenceStepTypePure[];
  voiceEnabled: boolean;
  voiceCallingEnabled: boolean;
  /** Offer the switch_campaign step in the picker — the graph opts in on connect branches. */
  allowSwitchCampaign: boolean;
  editorExtras: EditorExtras;
  /**
   * Subject of the email this new step would thread onto (null when none precedes it). Only an
   * email touch reads it — it surfaces the "continue previous email thread" toggle.
   */
  previousEmailSubject: string | null;
  onCreateStep: (data: AddStepParams) => Promise<unknown>;
  onDone: () => void;
}) {
  const [pickedType, setPickedType] = useState<CadenceStepTypePure | null>(null);

  const handlePick = (type: CadenceStepTypePure) => {
    if (planStepCreate(type).kind === 'fast-create') {
      onDone();
      void onCreateStep(buildFastCreateParams(type, { delay, delayAlignWeek, afterStepId, beforeStepId, option }));
      return;
    }
    setPickedType(type);
  };

  if (pickedType == null) {
    return (
      // No `onCancel` — the EditorCard's close ✕ (and Escape) already dismiss the
      // popup, so the picker's own "cancel" would be a redundant second control.
      <TemplateStepPickerPure
        existingTypes={existingTypes}
        voiceEnabled={voiceEnabled}
        voiceCallingEnabled={voiceCallingEnabled}
        allowSwitchCampaign={allowSwitchCampaign}
        onPickType={handlePick}
      />
    );
  }

  return (
    <CTSContentEditingPure
      stepType={pickedType as StepType}
      stepData={{}}
      variables={editorExtras.variables}
      documents={editorExtras.documents}
      reminderActions={editorExtras.reminderActions}
      reminderActionsLoading={editorExtras.reminderActionsLoading}
      onCreateReminder={editorExtras.onCreateReminder}
      reminderCreating={editorExtras.reminderCreating}
      onReminderError={editorExtras.onReminderError}
      renderSiftConditionBuilder={editorExtras.renderSiftConditionBuilder}
      canPersonalize={editorExtras.canPersonalize}
      personalizeModelInfo={editorExtras.personalizeModelInfo}
      campaignTemplateOptions={editorExtras.campaignTemplateOptions}
      previousEmailSubject={previousEmailSubject}
      submitLabel="Add touch"
      onSubmit={async (data) => {
        onDone();
        await onCreateStep(
          buildAddParamsFromEditor(pickedType, data, { delay, delayAlignWeek, afterStepId, beforeStepId, option }),
        );
      }}
      onCancel={onDone}
    />
  );
}
